import ProjectMember from '@projectMembers/models/projectMember';
import { type AssetJSON } from './models/asset';
import { type UserJson } from '../../models/user/user';
import Asset from './models/asset';
import { idsFrom } from '../../lib/modelsUtils';

export const updateItemAssignees = ( {
	item, usersToAddIDs, usersToRemoveIDs, projectMembers = []
}: {
	item: AssetJSON,
	usersToAddIDs: number[],
	usersToRemoveIDs: number[],
	projectMembers?: ProjectMember[]
} ) => {
	const existingAssignees = item.assignees || [];
	const assigneesAfterRemove = existingAssignees.filter(
		( a: UserJson ) => !usersToRemoveIDs.includes( a.id )
	);
	const existingIds = assigneesAfterRemove.map( ( a: UserJson ) => a.id );
	const newUserIds = usersToAddIDs.filter( id => !existingIds.includes( id ) );

	const getFullUserInfo = ( userId: number ): UserJson | null => {
		const member = projectMembers.find( m => m.user.id === userId );
		if ( member ) {
			return member.user.toJson();
		}
		return null;
	};

	const newAssignees = newUserIds
		.map( id => getFullUserInfo( id ) )
		.filter( ( user ): user is UserJson => user !== null );

	return {
		...item,
		assignees: [ ...assigneesAfterRemove, ...newAssignees ]
	};
};

export const allHaveTheSameAssignees = ( assets: Asset[] ) => {
	if ( assets.length === 0 ) return true;
	const firstAssetAssigneeIds = idsFrom( assets[ 0 ].assignees );

	return assets.every( ( asset ) => {
		const assigneeIds = idsFrom( asset.assignees );

		return assigneeIds.length === firstAssetAssigneeIds.length
			&& assigneeIds.every( id => firstAssetAssigneeIds.includes( id ) );
	} );
};

export const isAssignedToAllAssets = ( userId: number, assets: Asset[] ) => (
	assets.every( asset => asset.assignees.some( assignee => assignee.id === userId ) )
);

export const isAssignedToSomeAssets = ( userId: number, assets: Asset[] ) => (
	assets.some( asset => asset.assignees.some( assignee => assignee.id === userId ) )
);

export const allWithoutAssignees = ( assets: Asset[] ) => (
	assets.every( asset => asset.assignees.length === 0 )
);

export const removeDuplicatesAssets = ( assets: Asset[] ): Asset[] => {
	const seenIds = new Set<number>();
	return assets.filter( ( asset ) => {
		if ( seenIds.has( asset.id ) ) {
			return false;
		}
		seenIds.add( asset.id );
		return true;
	} );
};

export const getUserNameInitials = ( name: string ): string => {
	const parts = name.trim().split( ' ' );
	if ( parts.length >= 2 ) {
		return ( parts[ 0 ][ 0 ] + parts[ parts.length - 1 ][ 0 ] ).toUpperCase();
	}
	return name.substring( 0, 2 ).toUpperCase();
};
